import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {WizardDataService} from "../../../services/wizard-data.service";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {NgClass, NgForOf, NgIf, UpperCasePipe} from "@angular/common";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {MatInput} from "@angular/material/input";
import {FormsModule} from "@angular/forms";


interface ComparisonRow {
    key: string;
    baseValue: string;
    targetValue: string;
    isNew: boolean;
}

@Component({
    selector: 'app-step-four-review',
    standalone: true,
    imports: [
        MatIcon,
        MatButton,
        MatFormField,
        MatLabel,
        MatSelect,
        MatOption,
        UpperCasePipe,
        NgForOf,
        CdkTextareaAutosize,
        MatInput,
        NgIf,
        FormsModule
    ],
    templateUrl: './step-four-review.component.html',
    styleUrl: './step-four-review.component.scss'
})
export class StepFourReviewComponent implements OnInit {
    @Output() startOver = new EventEmitter<void>();

    selectedLanguage: string;
    comparisonData: ComparisonRow[] = [];

    constructor(public wizardData: WizardDataService) {}

    ngOnInit(): void {
        this.mergeAllTranslations();
        if (this.wizardData.targetLanguages.length > 0) {
            this.selectedLanguage = this.wizardData.targetLanguages[0];
            this.prepareComparisonView(this.selectedLanguage);
        }
    }

    private deepMerge(target: object, source: object): object {
        const output = { ...target };

        if (this.isObject(target) && this.isObject(source)) {
            Object.keys(source).forEach(key => {
                if (this.isObject(source[key])) {
                    if (key in target && this.isObject(target[key])) {
                        output[key] = this.deepMerge(target[key] as object, source[key] as object);
                    } else {
                        output[key] = source[key];
                    }
                } else {
                    Object.assign(output, { [key]: source[key] });
                }
            });
        }

        return output;
    }

    private mergeAllTranslations(): void {
        this.wizardData.targetLanguages.forEach((lang) => {
            const originalTargetJson = this.wizardData.targetJsonMap.get(lang) || {};
            const result = this.wizardData.translationResults.get(lang);

            if (result?.newTranslations) {
                result.fullMergedJson = this.deepMerge(originalTargetJson, result.newTranslations);
            } else {
                if (result) result.fullMergedJson = originalTargetJson;
            }
        });
    }

    private prepareComparisonView(lang: string): void {
        this.comparisonData = [];
        if (!this.wizardData.baseJson) return;

        const flatBaseJson = this.flattenObject(this.wizardData.baseJson);
        const result = this.wizardData.translationResults.get(lang);
        const flatNewTranslations = result?.newTranslations ? this.flattenObject(result.newTranslations) : {};
        const fullMergedJson = result?.fullMergedJson;
        const flatFullMergedJson = fullMergedJson ? this.flattenObject(fullMergedJson) : {};

        for (const key in flatBaseJson) {
            if (Object.prototype.hasOwnProperty.call(flatBaseJson, key)) {
                this.comparisonData.push({
                    key: key,
                    baseValue: flatBaseJson[key],
                    targetValue: flatFullMergedJson[key] || '',
                    isNew: flatNewTranslations.hasOwnProperty(key),
                });
            }
        }
    }

    onLanguageChange(lang: string): void {
        this.selectedLanguage = lang;
        this.prepareComparisonView(lang);
    }

    onTranslationChange(key: string, newValue: string): void {
        const result = this.wizardData.translationResults.get(this.selectedLanguage);
        if (result?.fullMergedJson) {
            this.setValueFromPath(result.fullMergedJson, key, newValue);
        }
    }

    downloadResults(): void {
        if (this.wizardData.inputFormat === 'combined') {
            this.downloadCombinedFile();
        } else {
            this.downloadSeparateFiles();
        }
    }

    private downloadSeparateFiles(): void {
        this.wizardData.targetLanguages.forEach((lang) => {
            const result = this.wizardData.translationResults.get(lang);
            if (result?.fullMergedJson) {
                this.triggerDownload(result.fullMergedJson, `${lang}.json`);
            }
        });
    }

    private downloadCombinedFile(): void {
        const combinedOutput = {
            [this.wizardData.baseLanguage]: this.wizardData.baseJson,
        };

        this.wizardData.targetLanguages.forEach((lang) => {
            const result = this.wizardData.translationResults.get(lang);
            combinedOutput[lang] = result?.fullMergedJson || this.wizardData.targetJsonMap.get(lang) || {};
        });

        this.triggerDownload(combinedOutput, 'translations_updated.json');
    }

    private triggerDownload(content: object, filename: string): void {
        const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    private isObject(item: any): boolean {
        return (item && typeof item === 'object' && !Array.isArray(item));
    }

    trackByKey(index: number, item: ComparisonRow): string {
        return item.key;
    }

    private flattenObject(obj: object, prefix = ''): { [key: string]: string } {
        return Object.keys(obj).reduce((acc, k) => {
            const pre = prefix.length ? prefix + '.' : '';
            if (this.isObject(obj[k])) {
                Object.assign(acc, this.flattenObject(obj[k], pre + k));
            } else {
                acc[pre + k] = obj[k];
            }
            return acc;
        }, {});
    }

    private getValueFromPath(obj: object, path: string): any {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }

    private setValueFromPath(obj: object, path: string, value: any): void {
        const keys = path.split('.');
        const lastKey = keys.pop();
        const lastObj = keys.reduce((o, key) => o[key] = o[key] || {}, obj);
        if (lastObj && lastKey) {
            lastObj[lastKey] = value;
        }
    }
}
